import React from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Clock,
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
} from "lucide-react";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { useWorkflowRunById } from "../../hooks";
import { NotificationDetail } from "../../types";

export const WorkflowHistoryDetails: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = parseInt(searchParams.get("page") || "1");
  const itemsPerPage = PAGE_SIZE;

  // Fetch workflow run details
  const {
    data: workflowRunData,
    isLoading,
    isError,
  } = useWorkflowRunById(runId ? parseInt(runId) : 0);

  // Get notifications data (no filtering)
  const notifications = workflowRunData?.results || [];

  // Pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedNotifications = notifications.slice(startIndex, endIndex);

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "delivered":
        return {
          variant: "default" as const,
          className: "bg-green-100 text-green-800 border-green-200",
          icon: <CheckCircle className="h-3 w-3" />,
        };
      case "failed":
        return {
          variant: "destructive" as const,
          className: "bg-red-100 text-red-800 border-red-200",
          icon: <XCircle className="h-3 w-3" />,
        };
      case "pending":
        return {
          variant: "outline" as const,
          className: "bg-yellow-100 text-yellow-800 border-yellow-200",
          icon: <Clock className="h-3 w-3" />,
        };
      default:
        return {
          variant: "secondary" as const,
          className: "bg-gray-100 text-gray-800 border-gray-200",
          icon: <AlertCircle className="h-3 w-3" />,
        };
    }
  };

  const formatRecipient = (message: NotificationDetail["message"]) => {
    const recipients = Array.isArray(message.to) ? message.to : [message.to];
    const ccRecipients = message.cc || [];
    const bccRecipients = message.bcc || [];

    return {
      primary: recipients.join(", "),
      cc: ccRecipients.length > 0 ? ccRecipients.join(", ") : null,
      bcc: bccRecipients.length > 0 ? bccRecipients.join(", ") : null,
    };
  };

  // Define table columns
  const columns: TableColumn<NotificationDetail>[] = [
    {
      key: "recipient",
      header: "Recipient",
      width: "w-64",
      type: "custom",
      render: (_, row) => {
        const recipient = formatRecipient(row.message);
        return (
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900">
              {recipient.primary}
            </div>
            {recipient.cc && (
              <div className="text-xs text-gray-500">CC: {recipient.cc}</div>
            )}
            {recipient.bcc && (
              <div className="text-xs text-gray-500">BCC: {recipient.bcc}</div>
            )}
          </div>
        );
      },
    },
    {
      key: "channelType",
      header: "Channel",
      width: "w-24",
      type: "custom",
      render: (_, row) => (
        <Badge variant="outline" className="text-xs">
          {row.channelType}
        </Badge>
      ),
    },
    {
      key: "statusDisplay",
      header: "Status",
      width: "w-32",
      type: "custom",
      render: (_, row) => {
        const statusConfig = getStatusBadge(row.statusDisplay);
        return (
          <Badge
            variant={statusConfig.variant}
            className={statusConfig.className}
          >
            <div className="flex items-center gap-1">
              {statusConfig.icon}
              {row.statusDisplay}
            </div>
          </Badge>
        );
      },
    },
    {
      key: "message",
      header: "Message",
      width: "w-80",
      type: "custom",
      render: (_, row) => (
        <div className="space-y-1">
          {row.message.subject && (
            <div className="text-sm font-medium text-gray-900 line-clamp-1">
              {row.message.subject}
            </div>
          )}
          <div className="text-xs text-gray-600 line-clamp-2">
            {row.message.body}
          </div>
          {row.template && (
            <div className="text-xs text-blue-600">
              Template: {row.template.templateName}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "runTime",
      header: "Sent",
      width: "w-32",
      type: "custom",
      render: (_, row) => (
        <div className="text-sm text-gray-600">{row.runTime}</div>
      ),
    },
  ];

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  const runDetails = workflowRunData;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to History
        </Button>
      </div>

      {/* Notifications Table */}
      <GenericTable
        data={paginatedNotifications}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        pagination={{
          currentPage: currentPage,
          totalPages: Math.ceil(notifications.length / itemsPerPage),
          totalItems: notifications.length,
          itemsPerPage,
          startItem: startIndex + 1,
          endItem: Math.min(endIndex, notifications.length),
        }}
        onPageChange={handlePageChange}
        emptyMessage="No notifications found for this workflow run"
      />
    </div>
  );
};
